/**
 * Phone Input component for @ollaid/native-sso
 * Custom select overlay: dropdown shows ISO code, selected display shows only flag + dialCode
 */

import React, { useState, useRef, useEffect } from 'react';
import { COUNTRIES_SORTED_BY_CODE, getCountryByDialCode, DEFAULT_DIAL_CODE } from '../utils/countries';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  ccphone?: string;
  onCcphoneChange?: (value: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  placeholder?: string;
  lockCcphone?: boolean;
}

export function PhoneInput({
  value, onChange, ccphone = DEFAULT_DIAL_CODE, onCcphoneChange, disabled = false, readOnly = false, error, placeholder = '77 123 45 67', lockCcphone = false,
}: PhoneInputProps) {
  const normalizedCcphone = ccphone || DEFAULT_DIAL_CODE;
  const selectedCountry = getCountryByDialCode(normalizedCcphone) || COUNTRIES_SORTED_BY_CODE[0];
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || readOnly) return;
    const cleaned = e.target.value.replace(/\D/g, '');
    onChange(cleaned.slice(0, selectedCountry.digits));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (disabled || readOnly) return;
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\s+/g, '').replace(/\D/g, '');
    onChange(pasted.slice(0, selectedCountry.digits));
  };

  const handleSelect = (dialCode: string) => {
    if (disabled || readOnly) return;
    if (onCcphoneChange) onCcphoneChange(dialCode);
    setDropdownOpen(false);
  };

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', border: `2px solid ${error ? '#ef4444' : '#d1d5db'}`,
        borderRadius: '0.5rem', overflow: 'visible', opacity: (disabled || readOnly) ? 0.75 : 1,
        backgroundColor: (disabled || readOnly) ? '#f9fafb' : 'white', position: 'relative',
      }}>
        {onCcphoneChange && !lockCcphone ? (
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            {/* Display button: only flag + dialCode */}
              <button
              type="button"
              onClick={() => !(disabled || readOnly) && setDropdownOpen(!dropdownOpen)}
              disabled={disabled || readOnly}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.6rem',
                backgroundColor: '#f9fafb', borderRight: '1px solid #e5e7eb', border: 'none',
                cursor: (disabled || readOnly) ? 'not-allowed' : 'pointer', fontWeight: 500, color: '#374151',
                fontSize: '0.85rem', whiteSpace: 'nowrap',
              }}
            >
              <span style={{ fontSize: '1rem' }}>{selectedCountry.flag}</span>
              <span>{selectedCountry.dialCode}</span>
              <span style={{ fontSize: '0.625rem', marginLeft: '0.125rem' }}>▼</span>
            </button>

            {/* Dropdown list: shows CODE FLAG DIALCODE */}
            {dropdownOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, zIndex: 50,
                backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '0.375rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '15rem', overflowY: 'auto',
                minWidth: '12rem', marginTop: '0.25rem',
              }}>
                {COUNTRIES_SORTED_BY_CODE.map((c) => (
                  <button
                    key={`${c.code}-${c.dialCode}`}
                    type="button"
                      onClick={() => handleSelect(c.dialCode)}
                      style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%',
                      padding: '0.45rem 0.75rem', border: 'none', cursor: 'pointer', fontSize: '0.85rem',
                      backgroundColor: c.dialCode === normalizedCcphone ? '#f3f4f6' : 'transparent',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = c.dialCode === normalizedCcphone ? '#f3f4f6' : 'transparent')}
                  >
                    <span style={{ fontWeight: 600, color: '#374151', minWidth: '1.75rem' }}>{c.code}</span>
                    <span>{c.flag}</span>
                    <span style={{ color: '#6b7280' }}>{c.dialCode}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Locked view: only flag + dialCode */
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.6rem', backgroundColor: '#f9fafb', borderRight: '1px solid #e5e7eb' }}>
            <span style={{ fontSize: '1rem' }}>{selectedCountry.flag}</span>
            <span style={{ fontWeight: 500, color: '#374151' }}>{selectedCountry.dialCode}</span>
          </div>
        )}
          <input
          type="tel"
          inputMode="numeric"
          value={value}
          onChange={handleChange}
          onPaste={handlePaste}
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          style={{ flex: 1, padding: '0.45rem 0.6rem', fontSize: '0.95rem', border: 'none', outline: 'none', backgroundColor: 'transparent' }}
        />
      </div>
      {error && <p style={{ fontSize: '0.875rem', color: '#ef4444', marginTop: '0.25rem' }}>{error}</p>}
      <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
        {selectedCountry.digits} chiffres requis ({value.length}/{selectedCountry.digits})
      </p>
    </div>
  );
}

export default PhoneInput;
